import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { useFormPopupContext } from '../../contexts/FormPopupContext';
import { products } from '../../utils/data';
import HeroSection from '../../components/HeroSection';

const VisionScreen: React.FC = () => {
  const product = products.find(p => p.id === 'vision-screen');
  const { openFormPopup } = useFormPopupContext();

  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
  }, []);

  if (!product) {
    return <div>Product not found</div>;
  }

  return (
    <div className="min-h-screen">
      {/* CINEMATIC CANVAS HERO SECTION */}
      <HeroSection
        desktopImage="/images/hero-section-2.png"
        mobileImage="/images/mobile-hero-2.png"
        title={
          <>
            <span className="text-gradient">Parallax Vision</span>
            <span className="block text-3xl md:text-4xl text-secondary mt-2">
              Projection Screen
            </span>
          </>
        }
        subtitle="The Vision Projection Screen serves as the ultimate canvas for your cinematic experience. Featuring acoustically transparent materials and precision tensioning systems, it delivers flawless image reproduction while allowing sound to pass through unimpeded."
        tagline={`Investment: ${product.priceRange}`}
      >
        <motion.div 
          className="flex flex-col sm:flex-row gap-4 justify-center"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.7 }}
        >
          <button 
            onClick={openFormPopup}
            className="btn-primary text-base sm:text-lg"
          >
            Explore Canvas Options
          </button>
          <button 
            onClick={openFormPopup}
            className="btn-secondary text-base sm:text-lg"
          >
            Custom Sizing Consultation
          </button>
        </motion.div>
      </HeroSection>

      {/* CANVAS PHILOSOPHY SECTION */}
      <section className="py-20 luxury-gradient">
        <div className="container mx-auto px-6">
          <div className="max-w-6xl mx-auto">
            <div className="grid md:grid-cols-2 gap-16 items-center">
              <motion.div
                initial={{ opacity: 0, x: -50 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8 }}
                viewport={{ once: true }}
              >
                <h2 className="text-4xl md:text-5xl font-light mb-8 text-gradient">
                  The Foundation of Excellence
                </h2>
                <div className="space-y-6 text-lg text-secondary/90">
                  <p>
                    A master painter demands the finest canvas. A filmmaker requires the perfect 
                    screen. The Vision Projection Screen is more than a display surface—it's the 
                    precisely engineered foundation upon which cinematic artistry comes to life.
                  </p>
                  <p>
                    Every detail is optimized for image perfection: acoustically transparent 
                    materials that maintain sonic integrity, precision tensioning systems that 
                    ensure perfect flatness, and ambient light rejection technology that preserves 
                    contrast in any environment.
                  </p>
                  <p>
                    This is where vision becomes reality, where content transforms into experience.
                  </p>
                </div>
              </motion.div>
              
              <motion.div 
                className="relative max-w-2xl mx-auto"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 1, ease: "easeOut" }}
                viewport={{ once: true }}
              >
                {/* Clean image presentation with subtle border */}
                <div className="relative group">
                  <div className="bg-[#2A2A2A] rounded-lg p-4 border border-[#2A2A2A]">
                    <img 
                      src="/images/parallax-card-image.png"
                      alt="Parallax Vision Projection Screen"
                      className="w-full h-auto rounded"
                      loading="lazy"
                      decoding="async"
                    />
                  </div>
                  
                  {/* Subtle hover effect overlay */}
                  <div className="absolute inset-0 bg-gradient-radial from-highlight/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 rounded-lg pointer-events-none"></div>
                </div>
                
                {/* Premium caption styling */}
                <motion.div 
                  className="mt-8 text-center"
                  initial={{ opacity: 0 }}
                  whileInView={{ opacity: 1 }}
                  transition={{ duration: 1, delay: 0.5 }}
                  viewport={{ once: true }}
                >
                  <div className="text-highlight/60 text-xs uppercase tracking-[0.3em] mb-2">
                    Handcrafted Excellence
                  </div>
                  <div className="text-secondary/40 text-sm uppercase tracking-wider">
                    Cinematic Canvas
                  </div>
                </motion.div>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* ACOUSTIC TRANSPARENCY SHOWCASE */}
      <section className="py-20 bg-primary">
        <div className="container mx-auto px-6">
          <div className="max-w-7xl mx-auto">
            <motion.div 
              className="text-center mb-16"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <h2 className="text-4xl md:text-5xl font-light mb-6 text-gradient">
                Acoustically Transparent Innovation
              </h2>
              <p className="text-xl text-secondary/80 max-w-3xl mx-auto">
                Advanced materials that deliver flawless visuals while preserving perfect audio
              </p>
            </motion.div>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              {/* Acoustic Transparency */}
              <motion.div 
                className="glass-effect rounded-xl p-8 hover:scale-105 transition-transform duration-300"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                viewport={{ once: true }}
              >
                <div className="text-highlight text-2xl font-light mb-4">Zero Loss</div>
                <h3 className="text-xl font-semibold mb-4 text-gradient">Acoustic Transparency</h3>
                <p className="text-secondary/80">
                  Sound passes through unimpeded while maintaining perfect 
                  image quality and color accuracy.
                </p>
              </motion.div>
              
              {/* Precision Tensioning */}
              <motion.div 
                className="glass-effect rounded-xl p-8 hover:scale-105 transition-transform duration-300"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.1 }}
                viewport={{ once: true }}
              >
                <div className="text-highlight text-2xl font-light mb-4">±0.1mm</div>
                <h3 className="text-xl font-semibold mb-4 text-gradient">Precision Tensioning</h3>
                <p className="text-secondary/80">
                  Advanced tensioning system ensures perfect flatness 
                  for distortion-free image reproduction.
                </p>
              </motion.div>
              
              {/* Ambient Light Rejection */}
              <motion.div 
                className="glass-effect rounded-xl p-8 hover:scale-105 transition-transform duration-300"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                viewport={{ once: true }}
              >
                <div className="text-highlight text-2xl font-light mb-4">ALR Coating</div>
                <h3 className="text-xl font-semibold mb-4 text-gradient">Light Rejection Technology</h3>
                <p className="text-secondary/80">
                  Advanced coating preserves contrast and color 
                  accuracy even in ambient light conditions.
                </p>
              </motion.div>
              
              {/* Custom Sizing */}
              <motion.div 
                className="glass-effect rounded-xl p-8 hover:scale-105 transition-transform duration-300"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.3 }}
                viewport={{ once: true }}
              >
                <div className="text-highlight text-2xl font-light mb-4">100"-200"</div>
                <h3 className="text-xl font-semibold mb-4 text-gradient">Custom Sizing</h3>
                <p className="text-secondary/80">
                  Precisely manufactured to your exact specifications 
                  for perfect integration into any space.
                </p>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* INSTALLATION OPTIONS & CONFIGURATIONS */}
      <section className="py-20 luxury-gradient">
        <div className="container mx-auto px-6">
          <div className="max-w-6xl mx-auto">
            <motion.div 
              className="text-center mb-16"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <h2 className="text-4xl font-light mb-8 text-gradient">
                Tailored to Your Vision
              </h2>
              <p className="text-xl text-secondary/80 max-w-3xl mx-auto">
                Multiple aspect ratios, installation options, and custom sizing 
                to perfectly complement your cinematic environment
              </p>
            </motion.div>
            
            <div className="grid md:grid-cols-3 gap-12">
              {/* Aspect Ratios */}
              <motion.div 
                className="text-center"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                viewport={{ once: true }}
              >
                <div className="glass-effect rounded-xl p-8 mb-6">
                  <h3 className="text-xl font-semibold mb-4 text-gradient">Aspect Ratios</h3>
                  <div className="space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="text-secondary/80">16:9</span>
                      <span className="text-highlight">Standard Cinema</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-secondary/80">2.35:1</span>
                      <span className="text-highlight">Anamorphic</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-secondary/80">4:3</span>
                      <span className="text-highlight">Classic Format</span>
                    </div>
                  </div>
                </div>
              </motion.div>
              
              {/* Installation Types */}
              <motion.div 
                className="text-center"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.1 }}
                viewport={{ once: true }}
              >
                <div className="glass-effect rounded-xl p-8 mb-6">
                  <h3 className="text-xl font-semibold mb-4 text-gradient">Installation Options</h3>
                  <div className="space-y-3">
                    <div className="text-secondary/80">Fixed Frame</div>
                    <div className="text-secondary/80">Motorized Retractable</div>
                    <div className="text-secondary/80">Tab-Tensioned</div>
                  </div>
                </div>
              </motion.div>
              
              {/* Size Range */}
              <motion.div 
                className="text-center"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                viewport={{ once: true }}
              >
                <div className="glass-effect rounded-xl p-8 mb-6">
                  <h3 className="text-xl font-semibold mb-4 text-gradient">Size Range</h3>
                  <div className="space-y-3">
                    <div className="text-secondary/80">100" - 120" Diagonal</div>
                    <div className="text-secondary/80">130" - 150" Diagonal</div>
                    <div className="text-secondary/80">160" - 200" Diagonal</div>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* MATERIAL SCIENCE & ENGINEERING */}
      <section className="py-20 bg-primary">
        <div className="container mx-auto px-6">
          <div className="max-w-6xl mx-auto">
            <div className="grid md:grid-cols-2 gap-16 items-center">
              <motion.div 
                className="relative max-w-2xl mx-auto"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 1, ease: "easeOut" }}
                viewport={{ once: true }}
              >
                {/* Clean image presentation with subtle border */}
                <div className="relative group">
                  <div className="bg-[#2A2A2A] rounded-lg p-4 border border-[#2A2A2A]">
                    <img 
                      src="/images/parallax-precision-materials.png"
                      alt="Parallax Precision Materials"
                      className="w-full h-auto rounded"
                      loading="lazy"
                      decoding="async"
                    />
                  </div>
                  
                  {/* Subtle hover effect overlay */}
                  <div className="absolute inset-0 bg-gradient-radial from-highlight/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 rounded-lg pointer-events-none"></div>
                </div>
                
                {/* Premium caption styling */}
                <motion.div 
                  className="mt-8 text-center"
                  initial={{ opacity: 0 }}
                  whileInView={{ opacity: 1 }}
                  transition={{ duration: 1, delay: 0.5 }}
                  viewport={{ once: true }}
                >
                  <div className="text-highlight/60 text-xs uppercase tracking-[0.3em] mb-2">
                    Handcrafted Excellence
                  </div>
                  <div className="text-secondary/40 text-sm uppercase tracking-wider">
                    Advanced Materials
                  </div>
                </motion.div>
              </motion.div>
              
              <motion.div
                initial={{ opacity: 0, x: 50 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8 }}
                viewport={{ once: true }}
              >
                <h2 className="text-4xl font-light mb-8 text-gradient">
                  Precision Material Engineering
                </h2>
                <div className="space-y-6 text-lg text-secondary/90">
                  <p>
                    The Vision screen material represents years of acoustic and optical 
                    engineering. Our proprietary weave allows sound waves to pass through 
                    with zero degradation while maintaining exceptional image quality.
                  </p>
                  <p>
                    Each screen is manufactured to exacting tolerances, with precision 
                    tensioning systems that ensure perfect flatness and dimensional 
                    stability for decades of flawless performance.
                  </p>
                </div>
                
                <div className="mt-8 grid grid-cols-2 gap-6">
                  <div>
                    <h4 className="text-highlight font-semibold mb-2">Gain Range</h4>
                    <p className="text-secondary/80 text-sm">0.9 - 1.2 (model dependent)</p>
                  </div>
                  <div>
                    <h4 className="text-highlight font-semibold mb-2">Viewing Angle</h4>
                    <p className="text-secondary/80 text-sm">160° horizontal</p>
                  </div>
                  <div>
                    <h4 className="text-highlight font-semibold mb-2">Warranty</h4>
                    <p className="text-secondary/80 text-sm">10 years material</p>
                  </div>
                  <div>
                    <h4 className="text-highlight font-semibold mb-2">Maintenance</h4>
                    <p className="text-secondary/80 text-sm">Minimal required</p>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* TECHNICAL SPECIFICATIONS */}
      <section className="py-20 luxury-gradient">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <motion.h2 
              className="text-3xl font-light text-center mb-12 text-gradient"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              Engineering Specifications
            </motion.h2>
            
            <motion.div 
              className="glass-effect rounded-xl p-8"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <div className="space-y-6">
                {Object.entries(product.specifications).map(([key, value]) => (
                  <div key={key} className="flex justify-between items-center border-b border-secondary/10 pb-4">
                    <span className="text-secondary/80 font-medium">{key}</span>
                    <span className="text-white font-light">{value}</span>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* INSTALLATION PROCESS & CALL TO ACTION */}
      <section className="py-20 bg-primary">
        <div className="container mx-auto px-6">
          <div className="max-w-6xl mx-auto text-center">
            <motion.h2 
              className="text-4xl md:text-5xl font-light mb-8 text-gradient"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              Precision Installation Process
            </motion.h2>
            <motion.p 
              className="text-xl text-secondary/80 mb-16 max-w-3xl mx-auto"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              viewport={{ once: true }}
            >
              From measurement to calibration, our specialists ensure your Vision screen 
              achieves perfect integration and optimal performance.
            </motion.p>
            
            <div className="grid md:grid-cols-4 gap-8 mb-16">
              <motion.div 
                className="text-center"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                viewport={{ once: true }}
              >
                <div className="w-16 h-16 bg-gradient-radial from-highlight/30 to-transparent rounded-full mx-auto mb-4 flex items-center justify-center">
                  <span className="text-highlight text-xl">📐</span>
                </div>
                <h3 className="text-lg font-semibold mb-2">Site Survey</h3>
                <p className="text-secondary/80 text-sm">Precise measurement and planning</p>
              </motion.div>
              
              <motion.div 
                className="text-center"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.1 }}
                viewport={{ once: true }}
              >
                <div className="w-16 h-16 bg-gradient-radial from-highlight/30 to-transparent rounded-full mx-auto mb-4 flex items-center justify-center">
                  <span className="text-highlight text-xl">🎨</span>
                </div>
                <h3 className="text-lg font-semibold mb-2">Custom Manufacturing</h3>
                <p className="text-secondary/80 text-sm">Built to your exact specifications</p>
              </motion.div>
              
              <motion.div 
                className="text-center"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                viewport={{ once: true }}
              >
                <div className="w-16 h-16 bg-gradient-radial from-highlight/30 to-transparent rounded-full mx-auto mb-4 flex items-center justify-center">
                  <span className="text-highlight text-xl">🔧</span>
                </div>
                <h3 className="text-lg font-semibold mb-2">Expert Installation</h3>
                <p className="text-secondary/80 text-sm">Precision mounting and tensioning</p>
              </motion.div>
              
              <motion.div 
                className="text-center"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.3 }}
                viewport={{ once: true }}
              >
                <div className="w-16 h-16 bg-gradient-radial from-highlight/30 to-transparent rounded-full mx-auto mb-4 flex items-center justify-center">
                  <span className="text-highlight text-xl">✨</span>
                </div>
                <h3 className="text-lg font-semibold mb-2">Final Calibration</h3>
                <p className="text-secondary/80 text-sm">Optimized for your projector</p>
              </motion.div>
            </div>
            
            <motion.div 
              className="max-w-4xl mx-auto"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <h3 className="text-3xl font-light mb-8 text-gradient">
                Complete Your Cinematic Vision
              </h3>
              <p className="text-xl text-secondary/80 mb-12">
                Discover how the Vision Projection Screen serves as the perfect foundation 
                for your ultimate home cinema experience.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-6 justify-center">
                <button 
                  onClick={openFormPopup}
                  className="btn-primary text-lg px-12 py-4"
                >
                  Explore Screen Options
                </button>
                <button 
                  onClick={openFormPopup}
                  className="btn-secondary text-lg px-12 py-4"
                >
                  Custom Sizing Consultation
                </button>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Popup handled globally */}
    </div>
  );
};

export default VisionScreen; 